// src/services/dummyDataService.ts
export interface UserData {
    id: string;
    fullName: string;
    username: string;
    email: string;
    phone: string;
    address: string;
    website?: string;
    avatar: string;
    role: string;
    isVerified: boolean;
    premiumMember: boolean;
    joinDate: string;
    contentCount?: number;
    totalRevenue?: number;
    protectionScore?: number;
    licenseCount?: number;
    totalSpent?: number;
    casesHandled?: number;
    rating?: number;
    investmentCount?: number;
    roi?: number;
    featuredContents?: any[];
    recentContracts?: any[];
    recentActivities?: any[];
    contents?: any[];
    contracts?: any[];
    revenueData?: any[];
    violations?: any[];
    licenses?: any[];
    activities?: any[];
    analyticsData?: any;
}

class DummyDataService {
    private users: Map<string, UserData> = new Map();

    // Generate dummy user data
    generateUser(userId: string): UserData {
        const roles = ['right_owner', 'licensee', 'creator', 'admin', 'moderator', 'investor', 'distributor', 'legal', 'monitor', 'staff'];
        const randomRole = roles[Math.floor(Math.random() * roles.length)];

        const firstNames = ['Nguyễn', 'Trần', 'Lê', 'Phạm', 'Hoàng', 'Vũ', 'Võ', 'Đặng', 'Bùi', 'Đỗ'];
        const middleNames = ['Văn', 'Thị', 'Đức', 'Minh', 'Hữu', 'Kim', 'Thanh', 'Ngọc', 'Phương', 'Hồng'];
        const lastNames = ['A', 'B', 'C', 'D', 'E', 'Phúc', 'Tài', 'Lộc', 'Thịnh', 'An'];

        const randomFirstName = firstNames[Math.floor(Math.random() * firstNames.length)];
        const randomMiddleName = middleNames[Math.floor(Math.random() * middleNames.length)];
        const randomLastName = lastNames[Math.floor(Math.random() * lastNames.length)];

        const avatarNumber = (parseInt(userId.slice(-1) || '1') % 6) + 1;

        const userData: UserData = {
            id: userId,
            fullName: `${randomFirstName} ${randomMiddleName} ${randomLastName}`,
            username: `user${userId}`,
            email: `user${userId}@vietrro.com`,
            phone: `090${Math.floor(Math.random() * 10000000).toString().padStart(7, '0')}`,
            address: '123 Nguyễn Huệ, Quận 1, TP.HCM',
            website: Math.random() > 0.5 ? 'https://example.com' : undefined,
            avatar: `/src/assets/images/avatar/avatar-${avatarNumber}.jpg`,
            role: randomRole,
            isVerified: Math.random() > 0.3,
            premiumMember: Math.random() > 0.7,
            joinDate: new Date(2022 + Math.floor(Math.random() * 2), Math.floor(Math.random() * 12), Math.floor(Math.random() * 28) + 1).toISOString().split('T')[0],
        };

        // Add role-specific data
        if (['right_owner', 'creator'].includes(userData.role)) {
            userData.contentCount = Math.floor(Math.random() * 50) + 5;
            userData.totalRevenue = Math.floor(Math.random() * 50000000) + 1000000;
            userData.protectionScore = Math.floor(Math.random() * 30) + 70;
            userData.featuredContents = this.generateContents(3);
            userData.contents = this.generateContents(userData.contentCount || 0);
            userData.revenueData = this.generateRevenueData();
            userData.violations = this.generateViolations(5);
        }

        if (['licensee', 'distributor'].includes(userData.role)) {
            userData.licenseCount = Math.floor(Math.random() * 30) + 5;
            userData.totalSpent = Math.floor(Math.random() * 30000000) + 500000;
            userData.licenses = this.generateLicenses(userData.licenseCount || 0);
        }

        if (['admin', 'legal', 'moderator', 'monitor', 'staff'].includes(userData.role)) {
            userData.casesHandled = Math.floor(Math.random() * 200) + 50;
            userData.rating = Math.round((Math.random() * 1 + 4) * 10) / 10;
            userData.activities = this.generateActivities(20);
            userData.recentActivities = this.generateActivities(5);
        }

        if (userData.role === 'investor') {
            userData.investmentCount = Math.floor(Math.random() * 20) + 5;
            userData.roi = Math.round((Math.random() * 20 + 10) * 10) / 10;
        }

        if (userData.role === 'data_analyst') {
            userData.analyticsData = this.generateAnalyticsData();
        }

        userData.recentContracts = this.generateContracts(3);
        userData.contracts = this.generateContracts(10);

        this.users.set(userId, userData);
        return userData;
    }

    // Get user by ID
    getUserById(userId: string): UserData {
        if (!this.users.has(userId)) {
            return this.generateUser(userId);
        }
        return this.users.get(userId)!;
    }

    // Generate dummy contents
    private generateContents(count: number): any[] {
        const contents = [];
        const types = ['image', 'video', 'document', 'audio'];

        for (let i = 0; i < count; i++) {
            contents.push({
                id: `content-${i + 1}`,
                title: `Tác phẩm ${i + 1}`,
                type: types[Math.floor(Math.random() * types.length)],
                thumbnail: `/images/content/content${(i % 5) + 1}.jpg`,
                views: Math.floor(Math.random() * 5000) + 100,
                likes: Math.floor(Math.random() * 1000) + 50,
                createDate: new Date(2023, Math.floor(Math.random() * 12), Math.floor(Math.random() * 28) + 1).toISOString()
            });
        }
        return contents;
    }

    // Generate dummy contracts
    private generateContracts(count: number): any[] {
        const contracts = [];
        const statuses = ['active', 'pending', 'completed', 'expired'];

        for (let i = 0; i < count; i++) {
            contracts.push({
                id: `contract-${i + 1}`,
                title: `Hợp đồng #${String(i + 1).padStart(3, '0')}`,
                date: new Date(2024, Math.floor(Math.random() * 3), Math.floor(Math.random() * 28) + 1).toISOString(),
                status: statuses[Math.floor(Math.random() * statuses.length)],
                amount: Math.floor(Math.random() * 5000000) + 500000
            });
        }
        return contracts;
    }

    // Generate dummy activities
    private generateActivities(count: number): any[] {
        const activities = [];
        const actions = ['Uploaded new content', 'Contract signed', 'License purchased', 'Content reviewed', 'Violation reported'];

        for (let i = 0; i < count; i++) {
            activities.push({
                id: `activity-${i + 1}`,
                action: actions[Math.floor(Math.random() * actions.length)],
                date: new Date(2024, 2, 28 - i).toISOString(),
                details: `Details for activity ${i + 1}`
            });
        }
        return activities;
    }

    // Generate dummy revenue data
    private generateRevenueData(): any[] {
        const revenueData = [];
        const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

        for (let i = 0; i < 12; i++) {
            revenueData.push({
                month: months[i],
                revenue: Math.floor(Math.random() * 5000000) + 500000
            });
        }
        return revenueData;
    }

    // Generate dummy violations
    private generateViolations(count: number): any[] {
        const violations = [];
        const types = ['copyright', 'trademark', 'unauthorized use', 'plagiarism'];
        const statuses = ['reported', 'investigating', 'resolved', 'dismissed'];

        for (let i = 0; i < count; i++) {
            violations.push({
                id: `violation-${i + 1}`,
                type: types[Math.floor(Math.random() * types.length)],
                status: statuses[Math.floor(Math.random() * statuses.length)],
                date: new Date(2024, Math.floor(Math.random() * 3), Math.floor(Math.random() * 28) + 1).toISOString(),
                description: `Vi phạm bản quyền #${i + 1}`
            });
        }
        return violations;
    }

    // Generate dummy licenses
    private generateLicenses(count: number): any[] {
        const licenses = [];
        const types = ['standard', 'extended', 'exclusive'];
        const statuses = ['active', 'expired', 'revoked'];

        for (let i = 0; i < count; i++) {
            licenses.push({
                id: `license-${i + 1}`,
                type: types[Math.floor(Math.random() * types.length)],
                status: statuses[Math.floor(Math.random() * statuses.length)],
                startDate: new Date(2023, Math.floor(Math.random() * 12), Math.floor(Math.random() * 28) + 1).toISOString(),
                endDate: new Date(2024, Math.floor(Math.random() * 12), Math.floor(Math.random() * 28) + 1).toISOString(),
                price: Math.floor(Math.random() * 2000000) + 100000
            });
        }
        return licenses;
    }

    // Generate dummy analytics data
    private generateAnalyticsData(): any {
        return {
            totalViews: Math.floor(Math.random() * 1000000) + 100000,
            totalDownloads: Math.floor(Math.random() * 50000) + 5000,
            totalRevenue: Math.floor(Math.random() * 100000000) + 10000000,
            activeUsers: Math.floor(Math.random() * 10000) + 1000,
            chartData: this.generateRevenueData()
        };
    }
}

export const dummyDataService = new DummyDataService();